import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ExtensionManifest, ExtensionSettingsValues } from '@/extensions/types';
import { resolveSettings } from '@/extensions/settings';

interface ExtensionSettingsFormProps {
  manifest: ExtensionManifest;
  values: ExtensionSettingsValues;
  onSave: (values: ExtensionSettingsValues) => Promise<boolean>;
}

/**
 * Renders a manifest's settings schema as a form. The parent remounts it (via
 * `key`) when saved values change, so the draft never fights the props.
 */
export function ExtensionSettingsForm({ manifest, values, onSave }: ExtensionSettingsFormProps) {
  const [draft, setDraft] = useState<ExtensionSettingsValues>(values);
  const [isSaving, setIsSaving] = useState(false);

  const dirty = Object.keys(manifest.settings).some((key) => String(draft[key]) !== String(values[key]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    // Clamp and type-check through the same resolver the app reads with.
    await onSave(resolveSettings(manifest, draft));
    setIsSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {Object.entries(manifest.settings).map(([key, field]) => {
        const id = `${manifest.id}-${key}`;
        return (
          <div key={key} className="space-y-1">
            <Label htmlFor={id} className="text-xs">{field.label}</Label>
            {field.type === 'select' ? (
              <Select
                value={String(draft[key] ?? field.default)}
                onValueChange={(v) => setDraft((d) => ({ ...d, [key]: v }))}
              >
                <SelectTrigger id={id} className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : field.type === 'number' ? (
              <div className="relative">
                <Input
                  id={id}
                  type="number"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={String(draft[key] ?? field.default)}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  className="h-8 text-sm pr-6"
                />
                {field.unit && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {field.unit}
                  </span>
                )}
              </div>
            ) : (
              <Input
                id={id}
                placeholder={field.placeholder}
                value={String(draft[key] ?? field.default)}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                className="h-8 text-sm"
              />
            )}
            {field.help && <p className="text-[11px] text-muted-foreground">{field.help}</p>}
          </div>
        );
      })}
      <div className="flex justify-end">
        <Button type="submit" size="sm" variant="outline" disabled={!dirty || isSaving}>
          {isSaving ? 'Saving...' : 'Save defaults'}
        </Button>
      </div>
    </form>
  );
}
